<template>
  <div class="quote-container smt-quote" v-loading="loading">
    <RestoreOrderAlert @restore="getParams" />
    <el-form id="quoteForm" class="quote-container__left" size="small" label-width="120px">
      <!-- 基本信息 -->
      <div class="quote-container__content">
        <div class="quote-container__header">
          PCBA下单
          <div class="quote-container__header__link">
            <BaseLink href="https://www.hqpcb.com/help/p_1721.html" target="_blank">
              一站式下单教程
            </BaseLink>
            <BaseLink href="https://smt.hqchip.com/equipment.html" target="_blank">
              华秋SMT工艺能力
            </BaseLink>
          </div>
        </div>
        <!-- BOM重新选型提示 -->
        <div v-if="isShowBomAlert" class="smt-quote__bomAlert">
          <i class="el-icon-warning"></i>
          修改【生产数量、PCB文件、BOM文件、坐标文件、贴装面】任意一个参数，有可能重新匹配BOM，请知悉
        </div>
        <!-- 产品领域 -->
        <application_sphere ref="application_sphere" v-model="smtParams.application_sphere" />
        <!-- PCB -->
        <custom_pcb_ban 
          ref="custom_pcb_ban"
          v-model="smtParams.custom_pcb_ban"
          :item="smtParams.pcbParams"
          :filePcb="smtParams.file_pcb"
          :pcbFileName="smtParams.pcb_file_name"
          @change="handlePcbChange" 
          @fileChange="handleFilePcbChange"
          @pcbaFileAnalyzeStatusChange="handlePcbaFileAnalyzeStatusChange"
          @analyzeChange="handleAnalyzeChange"
        />
        <!-- BOM物料 -->
        <buy_bom
          ref="buy_bom"
          v-model="smtParams.buy_bom"
          :item="{
            id: smtParams.order_info.bom_id,
            bomSn: smtParams.order_info.bom_sn,
            fileUrl: smtParams.file_bom,
            bomTitle: smtParams.bom_file_name,
          }"
          :bomFile="{
            url: smtParams.file_bom,
            name: smtParams.bom_file_name
          }"
          :smtFile="{
            url: smtParams.file_patch,
            name: smtParams.smt_file_name
          }"
          :bomId="smtParams.order_info.bom_self_id"
          :isParsingCoord="isParsingCoordFromPCB"
          :cacheFile="smtParams.cache_file"
          :isAutoAnalyze="smtParams.is_auto_analyze"
          :coordStatus="coordStatus"
          :customPcbBan="smtParams.custom_pcb_ban"
          @change="handleBomChange" 
          @bomFileChange="handleBomFileChange"
          @smtFileChange="handleSmtFileChange"
          @beforeLogin="handleBeforeLogin"
          @useCachedAnalysis="handleUseCachedAnalysis"
        />
      </div>
      <!-- PCB信息 -->
      <div v-if="smtParams.custom_pcb_ban === 1" class="quote-container__content">
        <i class="quote-container__linker"></i>
        <p class="quote-container__title">PCB信息</p>
        <PcbForm 
          ref="pcbFormRef" 
          v-model="smtParams.pcb_board_type" 
          :initPcbParamsChange="initPcbParamsChange" 
          :initFpcParamsChange="initFpcParamsChange"
        />
      </div>
      <!-- SMT信息 -->
      <div class="quote-container__content">
        <i class="quote-container__linker"></i>
        <p class="quote-container__title">SMT信息</p>
        <!-- 生产数量 -->
        <number
          key = "2"
          ref="number" 
          :units = "pcbParams.units"
          :customPcbBan = "smtParams.custom_pcb_ban"
          :number.sync="smtParams.number" 
          :number_type.sync="smtParams.number_type" 
          :isShowNumberType="isShow.number_type"
          :pcbNumber="pcbNumber"
        />
        <template v-if="smtParams.custom_pcb_ban === 3">
          <!-- PCB板类型 -->
          <is_pcb_soft_board ref="is_pcb_soft_board" v-model="smtParams.is_pcb_soft_board" />
          <!-- PCB拼版规则 -->
          <pcb_ban_layout
            ref="pcb_ban_num" 
            :pcb_ban_x.sync="smtParams.pcb_ban_x" 
            :pcb_ban_y.sync="smtParams.pcb_ban_y" 
          />
          <!-- PCB拼版尺寸 -->
          <pcb_ban_size
            ref="pcb_ban_size" 
            :pcb_ban_width.sync="smtParams.pcb_ban_width" 
            :pcb_ban_height.sync="smtParams.pcb_ban_height" 
          />
        </template>
        <!-- 单/双面 -->
        <patch
          ref="patch"
          :single_or_double_technique.sync="smtParams.single_or_double_technique"
          :bom_material_type_number.sync="smtParams.bom_material_type_number"
          :disabledTechnique="smtParams.order_info.is_mounting_surface_type"
        />
        <!-- 插件料 -->
        <is_plug
          ref="is_plug"
          :is_plug.sync="smtParams.is_plug" 
          :is_assembly_weld_for_tin.sync="smtParams.is_assembly_weld_for_tin" 
        />
        <!-- 钢网 -->
        <steel_type 
          ref="steel_type"
          :steel_type.sync="smtParams.steel_type" 
          :steel_no_tin_open.sync="smtParams.steel_no_tin_open" 
          :steel_remark.sync="smtParams.steel_remark" 
          :steel_position.sync="smtParams.steel_position" 
          :file_steel_url.sync="smtParams.file_steel_url" 
          :file_steel_name.sync="smtParams.file_steel_name" 
        />
        <!-- X-Ray -->
        <x_ray
          ref="x_ray"
          :x_ray_unit_number.sync="smtParams.x_ray_unit_number"
          :x_ray_number.sync="smtParams.x_ray_number"
          :number="smtParams.number_for_pcb || smtParams.number"
        />
        <!-- 刷三防漆 -->
        <need_conformal_coating 
          ref="need_conformal_coating"
          :need_conformal_coating.sync="smtParams.need_conformal_coating" 
          :is_dodge_solder_joint.sync="smtParams.is_dodge_solder_joint" 
          :need_conformal_coating_remark.sync="smtParams.need_conformal_coating_remark" 
          :file_conformal_url.sync="smtParams.file_conformal_url" 
          :file_conformal_name.sync="smtParams.file_conformal_name" 
        />
        <!-- 制程要求 -->
        <solder_paste_type ref="solder_paste_type" v-model="smtParams.solder_paste_type" />
        <!-- 特殊工艺 -->
        <process
          :is_plug="smtParams.is_plug"
          :is_assembly_weld.sync="smtParams.is_assembly_weld"
          :is_material_baking.sync="smtParams.is_material_baking"
          :is_assemble.sync="smtParams.is_assemble"
          :need_split.sync="smtParams.need_split"
          :is_layout_cleaning.sync="smtParams.is_layout_cleaning"
          :is_welding_wire.sync="smtParams.is_welding_wire"
          :is_increase_tinning.sync="smtParams.is_increase_tinning"
          :is_test.sync="smtParams.is_test"
          :is_program_burning.sync="smtParams.is_program_burning"
        />
        <!-- 样品图确认 -->
        <is_first_confirm 
          ref="is_first_confirm" 
          v-model="smtParams.is_first_confirm" 
          :is_material_baking="smtParams.is_material_baking"
          :is_layout_cleaning="smtParams.is_layout_cleaning"
        />
        <!-- 包装类型 -->
        <packing_type 
          v-model="smtParams.packing_type" 
          :need_split="smtParams.need_split" 
          :pcbSize="pcbSize" 
        />
        <!-- 工艺备注 -->
        <postscript 
          :postscript.sync="smtParams.postscript"
          :file_postscript.sync="smtParams.file_postscript"
          :file_postscript_name.sync="smtParams.file_postscript_name"
        />
      </div>
    </el-form>
    <affix :offset="89" relativeContainerId="quoteForm">
      <QuoteRight ref = "quoteRightRef" :type="quoteType" @handleOpenModal = "handleOpenModal" :pcbNumber = "pcbNumber" />
    </affix>
    <patchDialog ref = "patchDialogRef" :allPatchFlag = "isShow.number_type" :pcbNumber = "pcbNumber" @confirm= "handleConfirm" />
  </div>
</template>

<script>
// 右侧固钉悬浮
import Affix from '@@/components/affix/index'
// PCB信息
import PcbForm from './pcbForm/index'
// 计价页右侧
import QuoteRight from '@@/views/quoteRight/index'
// 恢复订单提示
import RestoreOrderAlert from './components/restoreOrderAlert'
import patchDialog from "./components/patchDialog.vue";
import { PCB_PARAMS_SESSION_KEY, PCBA_PARAMS_BEFORE_LOGIN_SESSION_KEY ,PCBA_PARAMS_BEFORE_RETURN_SESSION_KEY} from '@@/utils/constant'
import { debounce } from '@@/utils/utils'
import { scrollTop } from '@@/utils/pageScroll'
import { getNumberOptionKeys ,getFields} from '@@/utils/getFields'
import { ORDER_TYPE_ENUMS } from '@@/utils/constant'
import { SMT_DEFAULT_FORM, FR4_DEFAULT_FORM, FPC_DEFAULT_FORM, FR4_BOARD_DEFAULT_FORM } from '@@/utils/defaultForm'
import { formatPcbParams, computePcbSize, pcsNumForSet } from '@@/utils/pcbParamsFormat'
import { formatFpcParams } from '@@/utils/fpcParamsFormat'
import { formatSmtParams, isShowSmtParams } from '@@/utils/smtParamsFormat'
import { getPcbPrice } from '@@/api/pcb'
import { getSmtQuote, getSmtPrice } from '@@/api/smt'
import { getPcbaPrice, getOrderTemp, getPcbOrdersToBind ,pushPcbFileAnalysis,getCamSolderJoint} from '@@/api/pcba'

// 自动注册 fields 文件夹下的订单字段组件
const requireFields = require.context('./fields', true, /\.vue$/)
let fieldsComponents = {}
requireFields.keys().forEach(fileName => {
  let component = requireFields(fileName)
  fieldsComponents[fileName.replace(/^\.\/(.*)\.\w+$/, '$1')] = component.default
})

export default {
  components: {
    ...fieldsComponents,
    Affix,
    patchDialog,
    PcbForm,
    QuoteRight,
    RestoreOrderAlert
  },
  data() {
    return {
      // 当前区域是否自动解析BOM和坐标文件
      // isAutoAnalyze:false,
      // 属性用于标识 PCB 坐标变更状态（1: PCB已变更，坐标已更新，2: PCB已变更，请检查坐标文件）
      coordStatus:0,
      // 记录上一个pcb文件
      preFilePcb:'',
      // 上传文件的分析状态：true表示正在分析上传的文件，false表示分析完成或未开始
      // analyzeingUploadFile:false,
      isParsingCoordFromPCB: 1,  // 是否正在从PCB解析坐标文件 状态： 0-未开始, 1-上传中, 2-上传成功, 3-上传失败, 4-解析成功, 5-解析失败, 6-修改回显
      loading: false,
      smtParams: { ...SMT_DEFAULT_FORM, order_info: { ...SMT_DEFAULT_FORM.order_info }, pcb_board_type: 'FR-4' }, // pcb_board_type：PCB板材类型(前端字段)
      pcbParams: { ...FR4_DEFAULT_FORM, is_need_smt: 1, lack_handle_type: 0, place_source_type: 'ONE_STOP' }, // lack_handle_type: PCBA下单 少数处理方式 默认不设置
      fpcParams: { ...FPC_DEFAULT_FORM, is_need_smt: 1, place_source_type: 'ONE_STOP' },
      pcbPrice: {}, // PCB价格
      smtPrice: {}, // SMT价格
      priceTotal: {}, // 订单总计价格
      testOptions: [],
      debounceGetPirce: debounce(this.getPrice, 300), // 计价接口防抖
      initPcbParamsChange: false, // 返回修改初始化参数时，参数不进行关联变更
      initFpcParamsChange: false, // 返回修改初始化参数时，参数不进行关联变更
      oldBomFieldsData: {}, // 修改时需要重新去BOM选项的相关字段
      oldSmtFileData: {}, // BOM物料切换时，暂存BOM文件、坐标文件
    }
  },
  // 向右侧侧边栏注入订单参数
  provide() {
    return {
      pcbParams: this.pcbParams,
      fpcParams: this.fpcParams,
      smtParams: this.smtParams,
      oldBomFieldsData: () => this.oldBomFieldsData,
      getPcbPrice: () => this.pcbPrice,
      getSmtPrice: () => this.smtPrice,
      getPriceTotal: () => this.priceTotal,
      smtValidate: valids => this.validate(valids),
      pcbValidate: valids => this.pcbValidate(valids),
      fpcValidate: valids => this.pcbValidate(valids),
    }
  },
  computed: {
   // 相关字段是否显示判断
    isShow() {
       if(this.smtParams.custom_pcb_ban===2){
        return isShowSmtParams(this.smtParams, this.smtParams.pcbParams)
       }
      return isShowSmtParams(this.smtParams, this.pcbParamsFormat)
    },
    // 是否BOM重新选型提示
    isShowBomAlert() {
      const { bom_self_id, one_stop_temp_id } = this.smtParams.order_info || {}
      return !!+bom_self_id && !!+one_stop_temp_id && this.$route.query.source !== 'bom'
    },
    analyzeingFileWithHasFile() { 
      return this.smtParams.file_patch&&!this.smtParams.is_auto_analyze&&!this.preFilePcb
    },
    // PCB + SMT一起下单
    isNeedPcb() {
      return this.smtParams.custom_pcb_ban === 1
    },
    quoteType() {
      const { isNeedPcb, smtParams } = this
      return isNeedPcb ? { 'FR-4': 'SMT_PCB', 'FPC': 'SMT_FPC' }[smtParams.pcb_board_type] : 'SMT'
    },
    // 格式化后的FR-4/FPC订单参数
    pcbParamsFormat() {
      const { isNeedPcb, smtParams, pcbParams, fpcParams } = this
      const formattedParams = isNeedPcb ? {
        'FR-4': formatPcbParams(pcbParams),
        'FPC': formatFpcParams(fpcParams)
      }[smtParams.pcb_board_type] || {} : {}
  
      return formattedParams
    },
    // PCB拼版尺寸
    pcbSize() {
      const { custom_pcb_ban, pcbParams, pcb_ban_width, pcb_ban_height } = this.smtParams
      const pcbSize = {
        // 在华秋定制
        1: computePcbSize(this.pcbParams),
        // 已在华秋下单
        2: { width: pcbParams.bwidth, length: pcbParams.blength },
        // 自己提供
        3: { width: pcb_ban_width, length: pcb_ban_height }
      }[custom_pcb_ban]
      return pcbSize || { width: 0, length: 0 }
    },
    // PCB单片数量
    pcbNumber() {
      if(this.smtParams.custom_pcb_ban===2){
        return pcsNumForSet(this.smtParams.pcbParams)
      }
      return pcsNumForSet(this.pcbParamsFormat)
    },
    // 全贴的PCB数量
    numberForPcb() {
      const { pcbNumber, isShow, smtParams } = this
      // PCB PCS数量
      this.smtParams.number_pcb_pcs = isShow.number_type ? pcbNumber : 0
      // 显示生产数量类型 且 选中PCB全贴时，生产数量为PCB单片数量
      return isShow.number_type && smtParams.number_type === 1 ? pcbNumber : ''
    }
  },
  watch: {
    pcbParams: {
      deep: true,
      handler: function (val) {
        this.debounceGetPirce()
      }
    },
    fpcParams: {
      deep: true,
      handler: function (val) {
        this.debounceGetPirce()
      }
    },
    smtParams: {
      deep: true,
      handler: function (val) {
        this.debounceGetPirce()
      }
    },
    // 全贴的PCB数量改变
    numberForPcb(newVal) {
      this.smtParams.number_for_pcb = newVal
    }
  },
  async mounted() {
    let { id, type, hash, pcb_board_type, pcb_sn,cache='0' } = this.$route.query
    // type = isNaN(Number(type)) ? type : ORDER_TYPE_ENUMS[type]
  
    if (id) {
      // 没有缓存数据，正常获取参数
      await this.getParams(id);
    }
    
    if (hash) {
      this.$nextTick(() => {
        const ref = { 1: 'pcbFormRef' }[+hash]
        // 滚动到 PCB信息
        this.$refs[ref] && this.$refs[ref].$el.scrollIntoView({ behavior: 'smooth' })
      })
    }
    if (pcb_board_type) {
      this.initFromPcbCache(pcb_board_type)
    }
    // 绑定PCB订单
    if (pcb_sn) {
      this.bindPcbOrder(pcb_sn)
    }
    if(cache==='1'){
      this.initFromBeforeReturnCache(id)
    }else{
       this.initFromBeforeLoginCache()
    }
    if(this.smtParams.pcbParams.order_id){
      this.bindPcbOrder(this.smtParams.pcbParams.order_id)
    }
  },
  activated() {
    const { pcb_board_type } = this.$route.query
    if (pcb_board_type) {
      this.initFromPcbCache(pcb_board_type)
    }
  },
  methods: {
   
    handleConfirm(){
      this.$refs.quoteRightRef.handleSave()
    },
    handleOpenModal(errInfo){
      this.$nextTick(() => {
        const result = JSON.parse(errInfo)
        const { bcount, layoutx, layouty ,units} = result
        Object.assign(this.smtParams.pcbParams, { layoutx, layouty, bcount, units })
        this.$refs.patchDialogRef.open(result)
      })
    },
    handleCoordStatusChange(status) { 
      this.$refs.buy_bom.getPrevCoordStatus(this.coordStatus)
      this.coordStatus = status
    },
    // BOM文件/坐标文件 上传时未登录
    handleBeforeLogin() {
      const { isNeedPcb, pcbParamsFormat, smtParams } = this
      const params = {
        ...(isNeedPcb ? { pcb_params: pcbParamsFormat } : {}),
        ...formatSmtParams(smtParams)
      }
      sessionStorage.setItem(PCBA_PARAMS_BEFORE_LOGIN_SESSION_KEY, JSON.stringify(params))
    },
    // 登录前缓存的参数
    initFromBeforeLoginCache() {
      const paramsCache = sessionStorage.getItem(PCBA_PARAMS_BEFORE_LOGIN_SESSION_KEY)
      if (paramsCache) {
        this.initSmtParams(JSON.parse(paramsCache))
        sessionStorage.removeItem(PCBA_PARAMS_BEFORE_LOGIN_SESSION_KEY)
      }
    },
    // 确认页缓存的参数
    initFromBeforeReturnCache(id) {
      const cachedData = sessionStorage.getItem(`${PCBA_PARAMS_BEFORE_RETURN_SESSION_KEY}_${id}`);
      if(cachedData){
        try {
          const result = JSON.parse(cachedData)
          console.log(`${PCBA_PARAMS_BEFORE_RETURN_SESSION_KEY}_${id}`,result)
          this.initSmtParams(result)
        } catch (e) {
          console.log('Error parsing cached data:', e)
        }
      }
    },
    // 从PCB/FPC计价页跳转过来时，填充缓存的PCB/FPC参数
    initFromPcbCache(pcb_board_type) {
      this.smtParams.pcb_board_type = pcb_board_type
      const pcbParamsCache = sessionStorage.getItem(PCB_PARAMS_SESSION_KEY)
      if (pcbParamsCache) {
        const cacheData = JSON.parse(pcbParamsCache) || {}
        if (pcb_board_type === 'FR-4') {
          this.initPcbParamsChange = true
          this.smtParams.file_pcb = cacheData.pcbfile || ''
          this.pcbParams = Object.assign(this.pcbParams, cacheData)
          this.$nextTick(() => {
            this.initPcbParamsChange = false
          })
        }
        if (pcb_board_type === 'FPC') {
          this.initFpcParamsChange = true
          this.fpcParams = Object.assign(this.fpcParams, cacheData)
          this.$nextTick(() => {
            this.initFpcParamsChange = false
          })
        }
      }
    },
    // BOM订单切换
    handleBomChange(buy_bom, item = {}) {
      // 关联BOM订单
      if (buy_bom === 3) {
        const { id, bomSn, bomTitle, fileUrl } = item
        this.smtParams.order_info.bom_id = id
        this.smtParams.order_info.bom_sn = bomSn
        // 切换到 已在华秋下单
        if (!bomSn) {
          // 清空坐标文件
          this.smtParams.file_patch = ''
          this.smtParams.smt_file_name = ''
        } else {
          // 确认选择BOM订单
          this.smtParams.file_bom = fileUrl
          this.smtParams.bom_file_name = bomTitle
        }
      } else {
        this.smtParams.order_info.bom_id = ''
        this.smtParams.order_info.bom_sn = ''
        // 还原暂存的 BOM文件、坐标文件
        this.smtParams.file_bom = this.oldSmtFileData.file_bom || ''
        this.smtParams.bom_file_name = this.oldSmtFileData.bom_file_name || ''
        this.smtParams.file_patch = this.oldSmtFileData.file_patch || ''
        this.smtParams.smt_file_name = this.oldSmtFileData.smt_file_name || ''
      }
      // 自助匹配选型
      if (buy_bom === 4) {
        // 当坐标文件为压缩文件时，置空且提示
        const isZip = this.smtParams.file_patch && ['.zip', '.rar'].some(item => this.smtParams.file_patch.includes(item))
        if (isZip) {
          this.smtParams.file_patch = ''
          this.smtParams.smt_file_name = ''
          this.$alert('自助匹配选型只支持xls、xlsx、csv格式的坐标文件，请重新上传。', '温馨提示', {
            confirmButtonText: '好的',
            lockScroll: false,
            center: true,
            customClass: 'alert-fields-tips',
            closeOnPressEscape: true,
          })
        }
      }
    },
    // BOM文件上传成功
    handleBomFileChange(data) {
      const { fileUrl, fileName, materialCount } = data || {}
      this.smtParams.file_bom = fileUrl
      this.smtParams.bom_file_name = fileName
      this.smtParams.bom_material_type_number = materialCount || ''
      // 暂存 BOM文件
      this.oldSmtFileData.file_bom = fileUrl
      this.oldSmtFileData.bom_file_name = fileName
    },
    // 坐标文件上传成功
    handleSmtFileChange(data,isAuto=false) {
      const { fileUrl, fileName } = data || {}
      this.smtParams.file_patch = fileUrl
      this.smtParams.smt_file_name = fileName
      this.smtParams.is_auto_analyze = isAuto
      if (fileUrl !== this.oldBomFieldsData.file_patch) {
        this.smtParams.order_info.is_mounting_surface_type = true
        this.smtParams.order_info.model_selection_type = 0
      }
      // 在pcb上传后，解析中上传坐标文件
      if(!isAuto){
        this.handleCoordStatusChange(0)
        // if(+this.isParsingCoordFromPCB===2){
        //   this.analyzeingUploadFile = true
        // }
        // else{
        //   this.handleResetCachedAnalysis()
        // }
      }
      
      // 暂存 坐标文件
      this.oldSmtFileData.file_patch = fileUrl
      this.oldSmtFileData.smt_file_name = fileName
      this.oldSmtFileData.is_auto_analyze = isAuto
    },
     // 一站式下单v3.2迭代-焊点规则
    async handlePointChange(item) { 
      try {
        const {  pcbfile_url } = item
        if(!pcbfile_url) return
        const pushPcbFileAnalysisRes = await pushPcbFileAnalysis({
          pcbFilePath:pcbfile_url
        })
        if (pushPcbFileAnalysisRes.retCode !== 0) {
          this.$message.error(pushPcbFileAnalysisRes.retMsg || '未知错误，请稍后重试')
          return 
        } 
        const taskId = pushPcbFileAnalysisRes.result&&pushPcbFileAnalysisRes.result.taskId
        // 使用轮询函数获取CAM焊点结果
        await this.pollCamSolderJoint(taskId)
      } catch (error) {
        console.log(error)
      }
    },
     /**
     * 轮询获取CAM焊点结果
     * @param {string} taskId - 任务ID
     */
    pollCamSolderJoint(taskId) {
      return new Promise((resolve, reject) => {
        let attempts = 0
        const maxAttempts = 60
        const interval = 1000 // 1秒
        
        const poll = async () => {
          attempts++
          try {
            const getCamSolderJointRes = await getCamSolderJoint({ taskId })
            if (getCamSolderJointRes.retCode !== 0) {
              reject(new Error(getCamSolderJointRes.retMsg || '未知错误，请稍后重试'))
              return
            }
            
            const smtOrder = getCamSolderJointRes.result
            if (smtOrder.isCompleted) {
              // 完成时更新数据
              const { patchPadNumber, patchPadNumberBot, patchPadNumberTop, plugNumber } = smtOrder
              this.smtParams.smt_order = {
                patch_pad_number: patchPadNumber,
                patch_pad_number_bot: patchPadNumberBot,
                patch_pad_number_top: patchPadNumberTop,
                plug_number: plugNumber
              }
              resolve(smtOrder)
              return
            }
            
            // 如果未完成且未超过最大尝试次数，则继续轮询
            if (attempts < maxAttempts) {
              setTimeout(poll, interval)
            } else {
              // 达到最大尝试次数
              reject(new Error('获取焊点数据超时，请稍后重试'))
            }
          } catch (error) {
            reject(error)
          }
        }
        
        // 开始轮询
        poll()
      })
    },
    // PCB订单切换
    handlePcbChange(custom_pcb_ban, item = {}) {
      // 切换到华秋下单
      if (custom_pcb_ban === 2) {
        const { order_id, pcbfile_url, pcbfile_name,coordPath } = item
        this.smtParams.order_info.pcb_order_sn = order_id
        if(order_id) {
          // 选择了订单再清空文件信息，因为要保留
          this.preFilePcb = this.smtParams.file_pcb
          this.smtParams = Object.assign(this.smtParams, {
            file_pcb: pcbfile_url, 
            pcb_file_name: pcbfile_name, 
            pcbParams: item 
          })
          this.handleResetCachedAnalysis()
          this.handleAnalyzeChangeCallback(coordPath)
        }
        this.handlePointChange(item)
      
        // 之前选择了已在华秋下单选择了订单又切换到别的tab就把之前选择的订单信息清空
      } else if (+this.smtParams.order_info.pcb_order_sn) {
        // 一起下单
         if(this.smtParams.cache_file){
          this.handleResetCachedAnalysis()
        }
        if(+this.smtParams.is_auto_analyze){
          this.handleCoordStatusChange(0)
          this.handleUseCachedAnalysis({ fileName:'', coordPath:null })
        }
        this.preFilePcb = this.smtParams.file_pcb
        this.smtParams.order_info.pcb_order_sn = ''
        this.smtParams.file_pcb = ''
        this.smtParams.pcb_file_name = ''
        this.smtParams.pcbParams = {}
       
      }
    },
    // PCB文件上传成功
    handleFilePcbChange({val, name}) {
      this.preFilePcb = this.smtParams.file_pcb
      this.smtParams.file_pcb = val
      this.smtParams.pcb_file_name = name
      if(+this.smtParams.custom_pcb_ban===3){
        this.handleCoordStatusChange(0)
      }
    },
    handlePcbaFileAnalyzeStatusChange(info){
      this.isParsingCoordFromPCB = +info.status
    },
  
    // PCB文件解析成功
    handleAnalyzeChange(data) {
      const { file_type, blength, bwidth,coordPath,smt_order } = data || {}
      // 解析成功后，需要先重置拼版工具相关的字段
      this.pcbParams = Object.assign(
        this.pcbParams, 
        FR4_BOARD_DEFAULT_FORM, 
        data, 
        (file_type === -1 ? {} : { length: blength, width: bwidth })
      )
      if(file_type===-1){
        this.handleCoordStatusChange(-1)
        return
      }
      // 一站式下单v3.2迭代-焊点规则
      this.smtParams.smt_order = smt_order
      // 如果 coordPath 存在，则提取文件名并在坐标文件上传组件中显示
      this.handleAnalyzeChangeCallback(coordPath)
    },
    handleAnalyzeChangeCallback(coordPath) { 
      // 如果 coordPath 存在，则提取文件名并在坐标文件上传组件中显示
      this.coordPath = coordPath
      if (coordPath) {
        this.handleCoordStatusChange(this.preFilePcb&&this.smtParams.file_patch? 1:0)
        // 从路径中提取文件名
        const fileName = coordPath.split('/').pop();
        // 更新 smtParams 中的坐标文件相关信息，这将通过 props 传递给 buy_bom 组件
        // console.log("this.smtParams.file_patch&&!this.smtParams.is_auto_analyze",
        //   this.smtParams.file_patch,this.smtParams.is_auto_analyze
        // )
        // is_auto_analyze是指目前所在区域是否自动解析坐标文件
        if(this.analyzeingFileWithHasFile){
          this.smtParams.cache_file = {
            fileName,
            coordPath,
            is_auto_analyze: true
          }
        }else{
          this.handleUseCachedAnalysis({ fileName, coordPath })
        }
      }else{
        if(this.smtParams.custom_pcb_ban===2){
          this.handleCoordStatusChange(0)
          this.handleUseCachedAnalysis({ fileName:'', coordPath:null })
        }else{
          this.handleResetCachedAnalysis()
          this.handleCoordStatusChange(this.preFilePcb&&this.smtParams.file_patch? 2:0)
        }
      }
    },
    // 重置缓存，清空缓存信息
    handleResetCachedAnalysis() { 
      // this.analyzeingUploadFile=false
      this.smtParams.cache_file=null;
    },
    handleUseCachedAnalysis(data) { 
      const { fileName, coordPath } = data
      this.handleResetCachedAnalysis()
      this.handleSmtFileChange({ fileUrl:coordPath, fileName },true)
    },
    // 需要转为 Number 类型的字段
    transformNumberFields(type, params, otherKeys = []) {
      const fields = getNumberOptionKeys(type)
      return [...fields, ...otherKeys].reduce((data, key) => {
        if (params[key] !== undefined) {
          data[key] = Number(params[key])
        }
        return data
      }, {})
    },
    // PCB参数处理
    initPcbParams(params) {
      this.initPcbParamsChange = true
      const transformNumberFields = this.transformNumberFields('FR-4', params)
      const { report, pressing, pressing_computer_id, pressing_data } = params
      this.pcbParams = Object.assign(this.pcbParams, params, transformNumberFields, {
        // 产品报告
        report: report ? report.split(',') : ['无需'],
        // 叠层结构：当有计算id时，前端拼上计算id，用于区分接口返回的选项
        pressing: pressing_computer_id ? `${pressing_computer_id}__${pressing}` : pressing,
        // 叠层结构计算选项查看数据
        pressing_data: pressing_computer_id ? pressing_data : null
      })
      
      this.$nextTick(() => {
        this.initPcbParamsChange = false
      })
    },
    // FPC参数处理
    initFpcParams(params) {
      this.initFpcParamsChange = true
      const transformNumberFields = this.transformNumberFields('FPC', params)
      // 补强类型
      const reinforceFields = ['reinforce_steel', 'reinforce_aluminium', 'reinforce_fr4', 'reinforce_fingerpi', 'reinforce_otherpi']
      this.fpcParams = Object.assign(this.fpcParams, params, transformNumberFields, {
        // 补强
        reinforce: reinforceFields.some(i => params[i] == 1) ? 1 : 0,
        // 产品报告
        report: params.report ? params.report.split(',') : ['无需']
      })
      this.$nextTick(() => {
        this.initFpcParamsChange = false
      })
    },
    // SMT参数处理
    initSmtParams(params) {
      const pcb_params = params.pcb_params || {}
      const smt_params = params.smt_params || {}
      const order_info = params.order_info || {}
      const { custom_pcb_ban, buy_bom } = smt_params
      pcb_params.board_type = pcb_params.board_type || 'FR-4'
      const pcb_board_type = pcb_params.board_type
      const transformNumberFields = this.transformNumberFields('SMT', smt_params, ['single_or_double_technique'])
      this.smtParams = Object.assign(this.smtParams, smt_params, transformNumberFields, {
        pcb_board_type,
        // 一站式下单v3.2迭代-焊点规则
        smt_order:smt_params.smt_order||null,
        order_info: {...order_info},
        pcbParams: custom_pcb_ban == 2 ? {...pcb_params} : {}
      })

      // BOM自助选型时，记录BOM选型相关的字段的值，用于比较是否有改变，有改变时需要重新去BOM选型
      const RESET_BOM_FIELDS = ['number', 'file_pcb', 'file_bom', 'file_patch', 'single_or_double_technique', 'buy_bom']
      this.oldBomFieldsData = RESET_BOM_FIELDS.reduce((data, key) => {
        data[key] = smt_params[key]
        return data
      }, {})

      // 暂存 BOM文件
      this.oldSmtFileData.file_bom = smt_params.file_bom
      this.oldSmtFileData.bom_file_name = smt_params.bom_file_name
      // 暂存 坐标文件
      this.oldSmtFileData.file_patch = smt_params.file_patch
      this.oldSmtFileData.smt_file_name = smt_params.smt_file_name
      // BOM物料删除了已在华秋下单，之前的已选择的重置到华秋帮我选项并且清空BOM文件
      if(this.smtParams.buy_bom === 3) {
        this.smtParams.buy_bom = 1
        this.smtParams.file_bom = ''
        this.smtParams.bom_file_name = ''
        this.oldSmtFileData.file_bom = ''
        this.oldSmtFileData.bom_file_name = ''
      }
      if (custom_pcb_ban == 1) {
        this.$nextTick(() => {
          if (pcb_board_type === 'FR-4') {
            this.initPcbParams(pcb_params)
          }
          if (pcb_board_type === 'FPC') {
            this.initFpcParams(pcb_params)
          }
        })
      }
    },
    // 获取订单信息
    async getParams(id) {
      try {
        this.loading = true
        if (id.length < 15) {
          await this.getOldSmtParams(id)
        } else {
          await this.getSmtParams(id)
        }
        this.$nextTick(() => {
          this.loading = false
        })
      } catch (error) {
        this.loading = false
      }
    },
    // 获取SMT订单信息
    async getSmtParams(one_stop_temp_id) {
      return await getOrderTemp({ one_stop_temp_id })
        .then(res => {
          if (!res) return false
          const { retCode, result, retMsg } = res
          if (retCode === 0) {
            // SMT参数处理
            this.initSmtParams(result)
          } else {
            this.$message.error(retMsg)
          }
        })
    },
    // 获取旧版SMT订单信息（兼容DFM下单）
    async getOldSmtParams(smtTmpId) {
      return await getSmtQuote({ smtTmpId })
        .then(res => {
          if (!res) return false
          const { suc, body, msg } = res
          if (suc) {
            // SMT参数处理
            this.initSmtParams(body)
          } else {
            this.$message.error(msg)
          }
        })
    },
    // 绑定PCB订单
    bindPcbOrder(pcb_sn) {
      this.loading = true
      getPcbOrdersToBind({ keyword: pcb_sn })
        .then(res => {
          if (!res) return
          const { retCode, result } = res
          if (retCode === 0) {
            const list = (result || {}).list || []
            if (list.length === 1) {
              const pcbParams = list[0]
              this.smtParams.custom_pcb_ban = 2
              this.handlePcbChange(2, pcbParams)
            }
          }
        })
        .finally(() => {
          this.loading = false
        })
    },
    
    // 计价
    async getPrice() {
      const { number, number_for_pcb, pcb_board_type } = this.smtParams
      const { length, width, bcount } = { 'FR-4': this.pcbParams, 'FPC': this.fpcParams }[pcb_board_type] || {}
      // 尺寸、数量没填时不能计价
      if (
        !number && !number_for_pcb &&
        (!length || !width || !bcount)
      ) {
        this.pcbPrice = {}
        this.smtPrice = {}
        this.priceTotal = {}
        return
      }
      
      const res = number || number_for_pcb ? await this.getSmtPrice() : await this.getPcbPrice()
      if (res) {
        const { pcb = {}, smt = {}} = res.detail || {}
        if (pcb_board_type === 'FR-4') {
          this.pcbParams.test = pcb.test
        }
        if (pcb_board_type === 'FPC') {
          this.fpcParams.test = pcb.test
        }
        this.pcbPrice = pcb
        this.smtPrice = smt
        this.priceTotal = {...res,bomOrderWideDiscount:res.bomOrderWideDiscount}
      } else {
        this.pcbPrice = {}
        this.smtPrice = {}
        this.priceTotal = {}
      }
    },
    // PCB 计价
    async getPcbPrice() {
      const params = this.pcbParamsFormat
      return await getPcbPrice(params)
        .then(res => {
          if (!res) return {}
          const { retCode, result } = res
          return retCode === 0 ? result : {}
        })
    },
    // SMT 计价
    async getSmtPrice() {
      const { isNeedPcb, pcbParamsFormat, smtParams } = this

      return await getPcbaPrice({
        ...(isNeedPcb ? { pcb_params: pcbParamsFormat } : {}),
        ...formatSmtParams(smtParams)
      })
        .then(res => {
          if (!res) return false
          const { retCode, result } = res
          return retCode === 0 ? result : false
        })
    },

    scrollIntoView(key) {
      if (this.$refs[key]) {
        // 滚动到元素上面偏移50px的位置
        const top = this.$refs[key].$el.getBoundingClientRect().top
        console.log('top', top)
        scrollTop(top, -50)
      }
    },
    validate(data) {
      const keyMap = {
        bom_material_type_number: 'patch',
      }
      data.forEach((item, index) => {
        const ref = this.$refs[keyMap[item.key] || item.key]
        if (ref && ref.isShowError) {
          // 显示错误提示样式
          ref.isShowError(true, index === 0)
        }
      })
      const fristItem = data[0]
      this.$message.closeAll()
      this.$message.error(fristItem.tips)
      this.scrollIntoView(keyMap[fristItem.key] || fristItem.key)
    },
    pcbValidate(data) {
      this.$nextTick(() => {
        this.$refs.pcbFormRef && this.$refs.pcbFormRef.$refs.componentRef.validate(data)
      })
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~@@/styles/quote-container.scss';

.smt-quote {
  flex-wrap: wrap;
  .quote-container__header {
    margin-bottom: 20px;
    .quote-container__header__link a {
      margin-left: 20px;
    }
  }
  .smt-quote__bomAlert {
    display: flex;
    align-items: center;
    margin: 0 30px 16px;
    height: 35px;
    font-size: 12px;
    color: #FF4D4F;
    border-radius: 5px;
    background-color: #FEF0F0;
    >i {
      margin: 0 10px 0 12px;
      font-size: 16px;
    }
  }
  .smt-quote__submit {
    text-align: right;
    .smt-quote__submit__price {
      margin: 10px 8px 4px 0;
      font-size: 13px;
      color: #111111;
      font-weight: 700;
      >span {
        color: $--color-primary;
        &:first-child {
          font-size: 15px;
          font-weight: 400;
        }
        &:last-child {
          font-size: 20px;
        }
      }
    }
    .smt-quote__submit__tips {
      margin-bottom: 10px;
      font-size: 12px;
      color: $--color-text-disabled;
    }
  }
}

</style>
